import assert from 'assert';
import pathToRegexp from 'path-to-regexp';
import qs from 'qs';
import { Observable } from 'rxjs';
import lget from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import zipObject from 'lodash/zipObject';
import { createLocation } from 'history';
import createMemoryHistory from 'history/createMemoryHistory';
import createBrowserHistory from 'history/createBrowserHistory';
import fell from '@dbk-legacy/fell';

import muster from '../../muster-core/src';
import { watchValues } from '../../muster-utils';

function identity(arg) { return arg; }

function isBrowser() {
  return (
    global.navigator && global.document && global.window &&
    global.navigator.product !== 'ReactNative'
  );
}

function createStack(initialLocation) {
  if (!initialLocation) {
    return [createLocation('/')];
  }

  if (Array.isArray(initialLocation)) {
    return initialLocation.map(createLocation);
  }

  return [createLocation(initialLocation)];
}

function getHistory(stack, options = {}) {
  let createHistory = createMemoryHistory;

  const initialEntries = stack.map(({ pathname }) => pathname);

  if (options.createHistory) {
    createHistory = options.createHistory;
  } else if (isBrowser()) {
    createHistory = createBrowserHistory;
  }

  const history = createHistory({
    initialEntries,
    initialIndex: (initialEntries.length - 1),
    ...options,
  });

  return history;
}

function parseLocation(location) {
  if (location.query !== undefined && location.search !== undefined) {
    throw new Error('Location cannot contain both `query` and `search` attributes');
  }

  if (location.query) {
    return {
      ...location,
      search: qs.stringify(location.query),
    };
  }

  return location;
}

function serialize(data) {
  return JSON.stringify(data, null, 2);
}

function parseRouteConfig(prefix,routeConfig) {
  return Object.keys(routeConfig).map((path) => {

    const newpath = prefix + path;
    const matcher = pathToRegexp(newpath);
    const keys = matcher.keys ? matcher.keys.map(({ name }) => name) : [];
    return { matcher, keys, routeConfig: routeConfig[path] };
  }, {});
}

/**
 * @param {Location|Location[]} initialLocation - Starting location
 * @param {object} options - configuration for history
 * @param {fell} logger - Customer logger to use, defaults to its own logger
 *
 * @returns {object} muster route handler
 *
 * Internal types:
 *
 * type LocationState = {
 *  [key: string]: any,
 * }
 *
 * type Location = {|
 *  pathname: string,
 *  state: LocationState,
 *  search: string,
 *  hash: string,
 * |}
 *
 * type History = {
 *  stack: Location[],
 *  currentLocation: Location,
 * }
 *
 * // Mutations
 * declare function push(pathname: string, state: LocationState): void
 * declare function replace(pathname: string, state: LocationState): void
 * declare function goBack(): void
 * declare function goForward(): void
 */
export default function musterHistory(initialLocation, prefixFolder ,options = {}, logger) {
  console.log("musterHistory: initialLocation:",initialLocation);
  const log = logger || fell.getLogger('muster-history');
  const stack = createStack(initialLocation);
  const history = getHistory(stack, options);
  const routeConfig = options.routeConfig;
  var webfolder = prefixFolder;
  if ( webfolder != "" ) 
    { webfolder  = "/" + prefixFolder; }


  assert(routeConfig === undefined || isPlainObject(routeConfig), (
    'Expected `routeConfig` to be a plain object. ' +
    `Instead received \`${serialize(routeConfig)}\``
  ));

  const routeMatchers = routeConfig ? parseRouteConfig(webfolder,routeConfig) : null;
  // debugger;
  const historyObservable = Observable.create((sub) => {
    history.listen(() => {
      sub.next(history);
    });
  }).startWith(history);

  function getHistoryAttribute(attr, transform = identity) {
    return ({ res }) =>
      historyObservable.map((hist) => res.value(transform(lget(hist, attr))));
  }

  function getRouteMatchFromPathname(pathname) {
    const match = routeMatchers.find(({ matcher }) => matcher.test(pathname));
    assert(match, (
      `Could not find any matches for \`currentLocation.pathname\`: \`${pathname}\` ` +
      'Ensure that the route config has a matching route.'
    ));
    return match;
  }

  return muster()
    .path('stack', getHistoryAttribute('entries'))
    .path('currentLocation.pathname', getHistoryAttribute('location.pathname'))
    .path('currentLocation.state', getHistoryAttribute('location.state'))
    .path('currentLocation.search', getHistoryAttribute('location.search'))
    .path('currentLocation.hash', getHistoryAttribute('location.hash'))
    .path('currentLocation.query', getHistoryAttribute(
      'location.search',
      (search) => qs.parse(search.substring(1))
    ))
    .path('canGoBack', ({ res }) => {
      if (!history.canGo) {
        log.warn('canGoBack is only supported for memory based histories ' +
          '(eg react-native), it will return false for other histories');
        return res.value(false);
      }

      return historyObservable.map((hist) => res.value(hist.canGo(-1)));
    })

    // Route dependant paths
    .path('currentLocation.params', ({ req, res }, { get }) => {
      if (routeConfig === undefined) {
        throw new Error(
          'Cannot access `params` if no `routeConfig` option is defined. ' +
          `Error in route \`${req.route}\`.`
        );
      }

      return watchValues(get('currentLocation.pathname')).map(([pathname]) => {
        let match;
        try {
          match = getRouteMatchFromPathname(pathname);
        } catch (e) {
          throw new Error(`${e.message} Error in \`${req.route}\``);
        }

        const { matcher, keys } = match;
        const values = matcher.exec(pathname).slice(1);
        return res.value(zipObject(keys, values));
      });
    })

    .path('currentLocation.route', ({ req, res }, { get }) => {
      if (routeConfig === undefined) {
        throw new Error(
          'Cannot access `route` if no `routeConfig` option is defined. ' +
          `Error in route \`${req.route}\`.`
        );
      }

      return watchValues(get('currentLocation.pathname')).map(([pathname]) => {
        let match;
        try {
          match = getRouteMatchFromPathname(pathname);
        } catch (e) {
          throw new Error(`${e.message} Error in \`${req.route}\``);
        }

        return res.value(match.routeConfig);
      });
    })

    // actions
    .path('push', {
      call({ req }) {


        const [location, clearStack] = req.args;
        if (clearStack) {
          if ('index' in history) {
            history.index = -1;
          } else {
            log.warn('The stack can only be cleared for memory based histories ' +
              '(eg react-native), the clearStack argument has no effect on other histories.;');
          }
        }

        var webPath = "";

        if(location.pathname){
          webPath = webfolder + location.pathname;
        }else
        {
          webPath = webfolder + location;
        }
        // debugger
        history.push(parseLocation( webPath ));
        return {};
      },
    })

    .path('replace', {
      call({ req }) {
        const [location] = req.args;

        var webPath = "";
        if(location.pathname){
          webPath = webfolder + "" + location.pathname;
        }else
        {
          webPath = webfolder + location;
        }
        // debugger;
        history.replace(parseLocation(webPath));
        return {};
      },
    })

    .path('mergeState', {
      call({ req }) {
        const [newState] = req.args;
        const currentLocation = history.location;

        history.replace({
          ...currentLocation,
          state: Object.assign({}, currentLocation.state, newState),
        });
        return {};
      },
    })

    .path('replaceState', {
      call({ req }) {
        const [newState] = req.args;
        const currentLocation = history.location;
        var webPath = webfolder + newState.pathname;


        history.replace({
          ...currentLocation,
          state: webPath,
        });
        return {};
      },
    })

    .path('goBack', {
      call() {
        history.goBack();
        return {};
      },
    })

    .path('goForward', {
      call() {
        history.goForward();
        return {};
      },
    });
}
